/** Strukturierter Web-Recherche-Nachweis (n8n-Gate → Portal). Nicht LEX/JUD. */

export interface WebSearchProofSource {
  title: string;
  url: string;
  snippet?: string;
}

export interface WebSearchProof {
  status: 'ok' | 'no_match' | 'stub_no_key' | 'rejected_query' | 'http_error';
  audit_id: string;
  web_stamp: string;
  card?: string;
  card_tokens?: number;
  sources: WebSearchProofSource[];
}

export const PORTAL_WEB_SEARCH_MARKER = 'portal-web-search:gate-v1';

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function parseSource(raw: unknown): WebSearchProofSource | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const title = text(o.title).slice(0, 160);
  const url = text(o.url);
  if (!title || !url.startsWith('https://')) return null;
  if (/localhost|127\.|\/Mandanten\/|nextcloud/i.test(url)) return null;
  const snippet = text(o.snippet).slice(0, 240);
  return snippet ? { title, url, snippet } : { title, url };
}

export function parseWebSearchProof(raw: unknown): WebSearchProof | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const status = text(o.status);
  const allowed = ['ok', 'no_match', 'stub_no_key', 'rejected_query', 'http_error'];
  if (!allowed.includes(status)) return null;
  const auditId = text(o.audit_id);
  const stamp = text(o.web_stamp);
  if (!/^sha256:[0-9a-f]{64}$/i.test(auditId)) return null;
  if (!/^\[WEB:[0-9A-F]{16}\]$/.test(stamp)) return null;
  const sources = (Array.isArray(o.sources) ? o.sources : [])
    .map(parseSource)
    .filter((item): item is WebSearchProofSource => item !== null)
    .slice(0, 5);
  const cardTokens = Number(o.card_tokens);
  return {
    status: status as WebSearchProof['status'],
    audit_id: auditId,
    web_stamp: stamp,
    card: text(o.card).slice(0, 2000) || undefined,
    card_tokens: Number.isFinite(cardTokens) ? cardTokens : undefined,
    sources,
  };
}

export function hasWebSearchProofData(proof?: WebSearchProof | null): boolean {
  return parseWebSearchProof(proof ?? null) !== null;
}

export function stripWebStampForDisplay(text: string): string {
  return String(text || '')
    .replace(/\s*\[WEB:[0-9A-F]{16}\]\s*/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
